import {
  FORM_TOKEN_CHIP_CLASSES,
  FORM_TOKEN_CHIP_REQUIRED_CLASSES,
  FORM_TOKEN_HINT_CLASSES,
  FORM_TOKEN_RAIL_CLASSES,
} from '../dynamic-form.tokens';

import { type FieldTokenRailProps } from './FieldTokenRail.types';

/**
 * The values a field may interpolate, offered as things to press.
 *
 * The alternative — documenting the placeholders in the field's description and
 * leaving the operator to type `{{suspensionUntil}}` correctly — is exactly the
 * lowest-common-denominator control U0 forbids: the platform knows the list, so
 * the list is a control rather than a paragraph.
 *
 * `onMouseDown` is prevented on purpose, and it is the whole reason this works.
 * A button takes focus on press, which blurs the field, and a blurred field
 * reports no useful selection — so every chip would append instead of inserting
 * where the operator was typing. Preventing the default keeps focus (and the
 * caret) where it is; the click still fires.
 */
export function FieldTokenRail({ tokens, label, isDisabled, onInsert }: FieldTokenRailProps) {
  return (
    <div role="group" aria-label={label} className={FORM_TOKEN_RAIL_CLASSES}>
      {tokens.map((token) => (
        <button
          key={token.token}
          type="button"
          disabled={isDisabled}
          // The chip's own accessible name says what pressing it DOES, because
          // "name" alone reads as a label rather than as an action.
          aria-label={`${label}: ${token.label}`}
          title={token.hint ?? token.token}
          className={FORM_TOKEN_CHIP_CLASSES}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            onInsert(token.token);
          }}
        >
          <span>{token.label}</span>
          {token.isRequired === true ? (
            // `aria-hidden`: the chip's own name already carries the label, and
            // "required" here would announce a requirement of the TOKEN rather
            // than of the field, which is a different sentence.
            <span aria-hidden className={FORM_TOKEN_CHIP_REQUIRED_CLASSES}>
              *
            </span>
          ) : null}
          {token.hint === undefined ? null : (
            <span aria-hidden className={FORM_TOKEN_HINT_CLASSES}>
              {token.hint}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
